/**
 * Change password page — shown on first login when must_change_password is set,
 * or voluntarily from settings.
 *
 * After successful password change, redirects to the home page.
 */

import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { apiClient } from '@/services/apiClient'
import { errorDetailText } from '@/utils/errorDetail'
import { Button, Input, Alert, AlertDescription } from '@/components/ui'
import { KeyRound } from 'lucide-react'

export function ChangePasswordPage() {
  const { user, accessToken, mustChangePassword, impersonation, setAuth, setMustChangePassword } = useAuthStore()
  const navigate = useNavigate()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // SA-906: outside the shell, so the View-as banner is not here to settle
  // anything. The target's password is theirs alone (the server refuses the
  // change under a view-as): go home.
  if (impersonation) {
    return <Navigate to="/" replace />
  }

  // Must be authenticated to access this page
  if (!user || !accessToken) {
    return <Navigate to="/login" replace />
  }

  // If password change is not required and user navigated here directly,
  // we still allow it (voluntary password change from a link).

  const validate = (): string | null => {
    if (newPassword.length < 8) {
      return 'New password must be at least 8 characters'
    }
    if (newPassword !== confirmPassword) {
      return 'Passwords do not match'
    }
    if (currentPassword === newPassword) {
      return 'New password must be different from current password'
    }
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)

    try {
      const response = await apiClient.fetch('/api/auth/me/password', {
        method: 'PUT',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      })

      if (!response.ok) {
        const body = await response.json().catch(() => null)
        throw new Error(errorDetailText(body, 'Failed to change password'))
      }

      const data = await response.json()

      // Update access token (password change issues a new session)
      if (data.access_token) {
        setAuth(user, data.access_token, {
          mustChangePassword: false,
          mustAcceptDisclaimer: useAuthStore.getState().mustAcceptDisclaimer,
        })
      } else {
        setMustChangePassword(false)
      }

      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning/10">
            <KeyRound className="h-6 w-6 text-warning" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            Change Password
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mustChangePassword
              ? 'You must set a new password before continuing.'
              : 'Update your password.'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            placeholder="Enter current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
            autoFocus
          />

          <Input
            label="New Password"
            type="password"
            placeholder="At least 8 characters"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            autoComplete="new-password"
            hint="Minimum 8 characters"
          />

          <Input
            label="Confirm New Password"
            type="password"
            placeholder="Re-enter new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            error={
              confirmPassword && newPassword !== confirmPassword
                ? 'Passwords do not match'
                : undefined
            }
          />

          <Button
            type="submit"
            className="w-full"
            isLoading={isSubmitting}
            disabled={!currentPassword || !newPassword || !confirmPassword}
          >
            Change Password
          </Button>
        </form>
      </div>
    </div>
  )
}
